import { Link } from '@tanstack/react-router';
import { AlertTriangle, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { trpc } from '../../lib/trpc';

export function BankSessionAlert() {
  const alertsQuery = trpc.enableBanking.sessionAlerts.useQuery(undefined, {
    staleTime: 5 * 60 * 1000, // 5 min
    refetchOnWindowFocus: false,
  });

  const healthQuery = trpc.sync.health.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const alerts = alertsQuery.data ?? [];
  const unhealthy = healthQuery.data?.needsAttention ?? [];
  if (alerts.length === 0 && unhealthy.length === 0) return null;

  const expired = alerts.filter((a) => a.level === 'expired');
  const warnings = alerts.filter((a) => a.level === 'warning');

  const disconnected = unhealthy.filter((a) => a.status === 'disconnected');
  const stalled = unhealthy.filter((a) => a.status !== 'disconnected');

  return (
    <div className="border-b border-border">
      {disconnected.length > 0 && (
        <AlertRow tone="negative" to="/open-banking" linkLabel="Koppla">
          <strong className="font-semibold">{disconnected.map((a) => a.name).join(', ')}</strong>{' '}
          synkas inte – kontot saknas i bankkopplingen. Siffrorna är ofullständiga tills kontot
          kopplas igen.
        </AlertRow>
      )}
      {stalled.length > 0 && (
        <AlertRow tone="warning" to="/activity" linkLabel="Visa">
          Synken för{' '}
          <strong className="font-semibold">{stalled.map((a) => a.name).join(', ')}</strong>{' '}
          {stalled.some((a) => a.status === 'failing')
            ? 'misslyckas'
            : 'har inte gått på över ett dygn'}
          {stalled[0]?.lastSuccessAt
            ? ` (senast ${new Date(stalled[0].lastSuccessAt).toLocaleDateString('sv-SE')})`
            : ''}
          .
        </AlertRow>
      )}
      {expired.length > 0 && (
        <AlertRow tone="negative" to="/open-banking" linkLabel="Förnya">
          Bankkopplingen till{' '}
          <strong className="font-semibold">{expired.map((a) => a.bankName).join(', ')}</strong> har
          gått ut. Synkronisering är stoppad.
        </AlertRow>
      )}
      {warnings.length > 0 && (
        <AlertRow tone="warning" to="/open-banking" linkLabel="Förnya">
          Bankkopplingen till{' '}
          <strong className="font-semibold">{warnings.map((a) => a.bankName).join(', ')}</strong>{' '}
          går ut om {Math.min(...warnings.map((a) => a.daysRemaining ?? 0))} dagar. Förnya för att
          undvika avbrott.
        </AlertRow>
      )}
    </div>
  );
}

function AlertRow({
  tone,
  to,
  linkLabel,
  children,
}: {
  tone: 'negative' | 'warning';
  to: '/open-banking' | '/activity';
  linkLabel: string;
  children: ReactNode;
}) {
  const Icon = tone === 'negative' ? XCircle : AlertTriangle;
  return (
    <div
      className={cn(
        'flex items-start gap-2 px-4 py-2.5 text-sm sm:items-center',
        tone === 'negative' ? 'bg-negative-soft text-negative' : 'bg-warning-soft text-warning',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0 sm:mt-0" />
      <span className="min-w-0 flex-1">{children}</span>
      <Link
        to={to}
        className="shrink-0 font-medium whitespace-nowrap underline-offset-4 hover:underline"
      >
        {linkLabel} →
      </Link>
    </div>
  );
}
